import { CheckCircle2, Star } from 'lucide-react';
import { FormEvent, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import { StatusNotice } from '../components/StatusNotice';
import { SelectMenu } from '../components/ui/SelectMenu';
import { api } from '../lib/api';

export function ReviewPage() {
  const [params] = useSearchParams();
  const subjectType = params.get('subjectType') === 'agency' ? 'agency' : 'hostel';
  const subjectId = params.get('subjectId') || '';
  const placementId = params.get('placementId') || undefined;
  const [rating, setRating] = useState(5);
  const [accuracy, setAccuracy] = useState(5);
  const [communication, setCommunication] = useState(5);
  const [subject, setSubject] = useState<{ name: string; slug: string } | null>(null);
  const [subjectLoading, setSubjectLoading] = useState(Boolean(subjectId));
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!subjectId) { setSubjectLoading(false); return; }
    setSubjectLoading(true);
    api.get<{ name: string; slug: string }>(`/api/reviews/subject?subjectType=${subjectType}&subjectId=${encodeURIComponent(subjectId)}`)
      .then(setSubject)
      .catch(cause => setError(cause instanceof Error ? cause.message : 'Could not find this review subject.'))
      .finally(() => setSubjectLoading(false));
  }, [subjectId, subjectType]);

  function chooseOverall(value: number) {
    setRating(value);
    setAccuracy(value);
    setCommunication(value);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError('');
    try {
      await api.post('/api/reviews', {
        placementId,
        subjectType,
        subjectId,
        rating,
        title: form.get('title') || undefined,
        body: form.get('body'),
        criteria: { accuracy, communication },
      });
      setDone(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not submit review.');
    } finally {
      setBusy(false);
    }
  }

  if (done) return <div className="page narrow-page"><div className="success-state"><div className="success-icon"><CheckCircle2/></div><h1>Review submitted</h1><p>Thank you for sharing your experience. The review will appear after moderation.</p><Link className="primary-button" to={subject ? `/${subjectType === 'agency' ? 'agencies' : 'hostels'}/${subject.slug}` : '/'}>Return to {subject?.name || subjectType}</Link></div></div>;

  return <div className="page narrow-page">
    <PageHeader eyebrow="Community reviews" title={subjectLoading ? 'Loading review details…' : subject ? `Review ${subject.name}` : `Review this ${subjectType}`} description={subjectType === 'agency' ? 'Share your experience with the agency’s communication, accuracy, professionalism and support.' : 'Share what you know about the rooms, water, electricity, security, location and overall value.'}/>
    <StatusNotice type="info">Anyone with a HostelGrid account can leave a review. Reviews are moderated for relevance, abuse and conflicts of interest.</StatusNotice>
    {error && <StatusNotice type="error">{error}</StatusNotice>}
    {!subjectId && <StatusNotice type="error">A review subject was not provided.</StatusNotice>}
    <form className="form-surface" onSubmit={submit}>
      <div className="form-section">
        <label><span>Overall rating</span><div className="star-picker">{[1,2,3,4,5].map(value => <button type="button" key={value} className={value <= rating ? 'active' : ''} onClick={() => chooseOverall(value)} aria-label={`${value} stars`}><Star fill={value <= rating ? 'currentColor' : 'none'}/></button>)}</div></label>
        <label><span>Review title</span><input name="title" maxLength={120} placeholder="Summarise your experience"/></label>
        <label><span>Your experience</span><textarea name="body" minLength={10} maxLength={3000} rows={7} required placeholder="Write a fair, specific and helpful review."/></label>
        <div className="form-grid">
          <label><span>{subjectType === 'agency' ? 'Information accuracy' : 'Room accuracy'}</span><SelectMenu ariaLabel="Choose information accuracy rating" value={String(accuracy)} onChange={value => setAccuracy(Number(value))} options={[5,4,3,2,1].map(value => ({value:String(value),label:`${value} out of 5`,description:value===5?'Completely accurate':value===4?'Mostly accurate':value===3?'Some details differed':value===2?'Several details differed':'Information was inaccurate'}))}/></label>
          <label><span>{subjectType === 'agency' ? 'Communication' : 'Management'}</span><SelectMenu ariaLabel="Choose communication or management rating" value={String(communication)} onChange={value => setCommunication(Number(value))} options={[5,4,3,2,1].map(value => ({value:String(value),label:`${value} out of 5`,description:value===5?'Excellent':value===4?'Good':value===3?'Acceptable':value===2?'Needs improvement':'Poor'}))}/></label>
        </div>
      </div>
      <div className="form-footer"><span className="muted">Keep the review factual. Personal information and abusive content will be removed.</span><button className="primary-button" disabled={busy || !subjectId || !subject}>{busy ? 'Submitting…' : 'Submit review'}</button></div>
    </form>
  </div>;
}
